import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, BookOpen, CreditCard, XCircle, AlertCircle, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ICON_MAP = {
  booking_confirmed: { icon: Check, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  booking_paid: { icon: CreditCard, color: 'text-blue-500', bg: 'bg-blue-50' },
  booking_cancelled: { icon: XCircle, color: 'text-gray-500', bg: 'bg-gray-50' },
  booking_rejected: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50' },
  info: { icon: Info, color: 'text-sky-500', bg: 'bg-sky-50' },
};

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  if (diff < 604800) return `vor ${Math.floor(diff / 86400)} Tagen`;
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const NotificationBell = () => {
  const { token, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notifications/unread-count`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count);
      }
    } catch (_) {}
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications?limit=15`, { headers });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.items || []);
      }
    } catch (_) {}
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markAsRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/notifications/${id}/read`, { method: 'PUT', headers });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, { method: 'PUT', headers });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const deleteNotification = async (id) => {
    try {
      await fetch(`${API_URL}/api/notifications/${id}`, { method: 'DELETE', headers });
      const wasUnread = notifications.find(n => n.id === id && !n.read);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (_) {}
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="notification-bell"
        aria-label="Benachrichtigungen"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-[380px] max-h-[480px] bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-sm text-gray-900">Benachrichtigungen</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                data-testid="mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Alle gelesen
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-[400px]">
            {loading && notifications.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">Laden...</div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Keine Benachrichtigungen</p>
              </div>
            ) : (
              notifications.map((n) => {
                const config = ICON_MAP[n.type] || ICON_MAP.info;
                const IconComp = config.icon;
                return (
                  <div
                    key={n.id}
                    className={`flex gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors group ${!n.read ? 'bg-blue-50/40' : ''}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`flex-shrink-0 w-9 h-9 rounded-full ${config.bg} flex items-center justify-center mt-0.5`}>
                      <IconComp className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        {n.link ? (
                          <Link
                            to={n.link}
                            className="font-medium text-sm text-gray-900 hover:text-blue-600 transition-colors truncate"
                            onClick={() => { if (!n.read) markAsRead(n.id); setOpen(false); }}
                          >
                            {n.title}
                          </Link>
                        ) : (
                          <span className="font-medium text-sm text-gray-900 truncate">{n.title}</span>
                        )}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!n.read && (
                            <button onClick={() => markAsRead(n.id)} className="p-0.5 hover:bg-blue-100 rounded" title="Als gelesen markieren">
                              <Check className="w-3.5 h-3.5 text-blue-500" />
                            </button>
                          )}
                          <button onClick={() => deleteNotification(n.id)} className="p-0.5 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100" title="Löschen">
                            <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                      <span className="text-[10px] text-gray-400 mt-1 block">{timeAgo(n.created_at)}</span>
                    </div>
                    {!n.read && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
